class NewArrivalsToolbar extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-new-arrivals-sort-trigger]');
    this.menu = this.querySelector('[data-new-arrivals-sort-menu]');
    this.valueElement = this.querySelector('[data-new-arrivals-sort-value]');
    this.options = Array.from(this.querySelectorAll('[data-new-arrivals-sort-option]'));
    this.status = this.querySelector('[data-new-arrivals-sort-status]');
    this.sortBar = this.querySelector('.new-arrivals-sort');
    this.placeholder = this.querySelector('.new-arrivals-sort-placeholder');
    this.isStickyEnabled = this.dataset.sticky === 'true';
    this.currentSortValue =
      this.options.find((option) => option.getAttribute('aria-selected') === 'true')?.dataset.sortValue || 'featured';

    if (!this.trigger || !this.menu || !this.options.length || !this.sortBar || !this.placeholder) return;

    this.rememberOriginalOrder(this.getItems());
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleDocumentClick);

    if (this.isStickyEnabled) {
      this.updateMeasurements();
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);
      this.handleScroll();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

  getSortScope() {
    return this.closest('main') || document;
  }

  getGrids() {
    return Array.from(this.getSortScope().querySelectorAll('.new-arrivals-products__grid'));
  }

  getItems(grids = this.getGrids()) {
    return grids.flatMap((grid) => Array.from(grid.querySelectorAll(':scope > .new-arrivals-products__item')));
  }

  rememberOriginalOrder(items) {
    items.forEach((item, index) => {
      if (item.dataset.newArrivalsOriginalOrder === undefined) {
        item.dataset.newArrivalsOriginalOrder = String(index);
      }
    });
  }

  compareItems(itemA, itemB) {
    const originalDifference =
      Number(itemA.dataset.newArrivalsOriginalOrder) - Number(itemB.dataset.newArrivalsOriginalOrder);

    if (this.currentSortValue === 'price-ascending' || this.currentSortValue === 'price-descending') {
      const priceA = Number(itemA.dataset.newArrivalsPrice) || 0;
      const priceB = Number(itemB.dataset.newArrivalsPrice) || 0;
      const priceDifference = priceA - priceB;

      if (priceDifference !== 0) {
        return this.currentSortValue === 'price-ascending' ? priceDifference : -priceDifference;
      }
    }

    return originalDifference;
  }

  sortProducts() {
    const grids = this.getGrids();
    const capacities = grids.map((grid) => grid.children.length);
    const items = this.getItems(grids);

    if (!grids.length || !items.length) return;

    this.rememberOriginalOrder(items);
    items.sort((itemA, itemB) => this.compareItems(itemA, itemB));

    let itemOffset = 0;
    grids.forEach((grid, gridIndex) => {
      const fragment = document.createDocumentFragment();
      const gridItems = items.slice(itemOffset, itemOffset + capacities[gridIndex]);

      gridItems.forEach((item) => fragment.appendChild(item));
      grid.appendChild(fragment);
      itemOffset += capacities[gridIndex];
    });

    const selectedOption = this.options.find((option) => option.dataset.sortValue === this.currentSortValue);

    if (this.status && selectedOption) {
      this.status.textContent = selectedOption.textContent.trim();
    }
  }

  setSortValue(sortValue) {
    const selectedOption = this.options.find((option) => option.dataset.sortValue === sortValue);
    if (!selectedOption) return;

    this.currentSortValue = sortValue;
    this.options.forEach((option) => {
      option.setAttribute('aria-selected', String(option === selectedOption));
    });

    if (this.valueElement) {
      this.valueElement.textContent = selectedOption.textContent.trim();
    }

    this.sortProducts();
    this.closeMenu();
    this.trigger.focus();
  }

  openMenu(focusSelected = false) {
    this.menu.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');

    if (focusSelected) {
      const selectedOption = this.options.find((option) => option.getAttribute('aria-selected') === 'true');
      selectedOption?.focus();
    }
  }

  closeMenu() {
    this.menu.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  toggleMenu() {
    if (this.menu.hidden) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  getHeaderOffset() {
    const header = document.querySelector('.section-header');
    if (!header) return 0;

    const rect = header.getBoundingClientRect();
    const style = getComputedStyle(header);
    const isSticky = style.position === 'sticky' || style.position === 'fixed';
    const isVisibleAtTop = rect.bottom > 0 && rect.top <= 1;

    return isSticky && isVisibleAtTop ? Math.max(0, rect.bottom) : 0;
  }

  updateHeaderOffset() {
    this.style.setProperty('--new-arrivals-header-offset', `${this.getHeaderOffset()}px`);
  }

  updateMeasurements() {
    const wasSticky = this.sortBar.classList.contains('is-sticky');

    if (wasSticky) {
      this.sortBar.classList.remove('is-sticky');
      this.placeholder.style.height = '';
    }

    this.sortBarHeight = this.sortBar.offsetHeight;
    this.originTop = this.placeholder.getBoundingClientRect().top + window.scrollY;
    this.updateHeaderOffset();

    if (wasSticky) this.setSticky(true);
  }

  setSticky(shouldStick) {
    this.sortBar.classList.toggle('is-sticky', shouldStick);
    this.placeholder.style.height = shouldStick ? `${this.sortBarHeight}px` : '';
  }

  handleClick(event) {
    const option = event.target.closest('[data-new-arrivals-sort-option]');
    if (option && this.contains(option)) {
      this.setSortValue(option.dataset.sortValue);
      return;
    }

    const trigger = event.target.closest('[data-new-arrivals-sort-trigger]');
    if (trigger && this.contains(trigger)) {
      this.toggleMenu();
    }
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closeMenu();
    }
  }

  handleKeydown(event) {
    if (event.target === this.trigger && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      this.openMenu(true);
      return;
    }

    const option = event.target.closest('[data-new-arrivals-sort-option]');
    if (!option || !this.contains(option)) {
      if (event.key === 'Escape') this.closeMenu();
      return;
    }

    const optionIndex = this.options.indexOf(option);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (optionIndex + direction + this.options.length) % this.options.length;
      this.options[nextIndex].focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.options[event.key === 'Home' ? 0 : this.options.length - 1].focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.setSortValue(option.dataset.sortValue);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
      this.trigger.focus();
    }
  }

  handleScroll() {
    this.updateHeaderOffset();
    const shouldStick = window.scrollY + this.getHeaderOffset() >= this.originTop;
    this.setSticky(shouldStick);
  }

  handleResize() {
    this.updateMeasurements();
    this.handleScroll();
  }
}

if (!customElements.get('new-arrivals-toolbar')) {
  customElements.define('new-arrivals-toolbar', NewArrivalsToolbar);
}
